import { Modal } from "@heroui/react";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { processDocumentFile, validateFile } from "core/model/files";
import { ALLOWED_FILE_MIME_TYPES } from "core/model/files/config";
import {
  AuctionRequest,
  FileHISStateful,
  FileState,
  Filev2,
  QueryProgress,
} from "core/types";
import { Button, ButtonProps } from "ds/ui/button/button";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import {
  FileUploadModalProps,
  FileUploadSubmitHandler,
} from "dsl/organisms/Modals/FileUploadModal";
import { FileUpIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { DragNDropScreen } from "./Screens/DragNDropScreen";
import { FileUploadScreen } from "./Screens/FileUploadScreen";
import { HISFileUploadScreen } from "./Screens/HISFileUploadScreen";

export type ProcessedFile = {
  content: string;
  file: File;
};

export function useFileUpload() {
  const { trackEvent } = useTracking();
  const toast = useToast();
  const isEligiblePage = useIsEligiblePage();

  const translations = useTranslations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [processedFile, setProcessedFile] = useState<ProcessedFile | null>(
    null,
  );

  function handleClick() {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }
  async function handleChange(file: File | undefined) {
    try {
      if (!file) {
        throw new Error(translations.fileUploader.fileUploadError);
      }

      await validateFile({ file, isEligiblePage, trackEvent, translations });

      const content = await processDocumentFile(file);
      setProcessedFile({ content, file });
    } catch (error) {
      toast({ color: "danger", message: (error as Error).message });
    }
  }

  function reset() {
    if (inputRef.current) {
      inputRef.current.value = "";
      setProcessedFile(null);
    }
  }

  return { handleChange, handleClick, inputRef, processedFile, reset };
}

export function UploadFilesModal({
  action,
  allowedFileTypes,
  availableFiles,
  getFilesHISProgress,
  handleSubmit,
  isDisabled = true,
  mutationProgress,
  reset,
  setAction,
  unavailableFiles,
  uploadButtonProps,
}: {
  action: GeneralListAction<Filev2> | undefined;
  allowedFileTypes?: readonly string[] | string[];
  auctionRequest?: AuctionRequest;
  availableFiles: FileState[] | null;
  getFilesHISProgress: QueryProgress;
  handleSubmit: FileUploadSubmitHandler;
  isDisabled: boolean;
  mutationProgress: QueryProgress;
  reset: () => void;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
  uploadButtonProps?: Omit<ButtonProps<false>, "ref" | "onPress">;
}) {
  const { app } = useEnvContext();
  const { isTablet } = useMedia();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const hasActiveOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );

  const {
    handleChange,
    handleClick,
    inputRef,
    processedFile,
    reset: resetFile,
  } = useFileUpload();

  const isEligiblePage = useIsEligiblePage();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;
  const { extractOcr } = useExtractDocs();

  const wasModalOpenRef = useRef(false);
  const isModalOpen = !!action?.type;

  async function submit(
    fileUploadData: Parameters<FileUploadModalProps["handleSubmit"]>[0],
  ) {
    handleSubmit(
      { ...processedFile, ...fileUploadData },
      {
        onComplete: async (fileId) => {
          if (fileUploadData.should_extract_on_upload && processedFile?.file) {
            await extractOcr.handleOcrForFile({
              file: processedFile.file,
              fileCategory: fileUploadData.category,
              id: fileId,
            });
          }
        },
        onStarted: () => {
          reset();
          resetFile();
        },
      },
    );
  }

  const close = useCallback(() => {
    reset();
    resetFile();
    setAction(undefined);
  }, [reset, resetFile, setAction]);

  const handleSelectFileV2 = useCallback(() => {
    if (app === APP_CAREPROVIDER) {
      if (isTablet) {
        handleClick();
      } else {
        setAction({
          data: {} as FileFeatureData,
          type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
        });
      }
    } else {
      setAction({
        data: {} as FileFeatureData,
        type: hasActiveOnPrem
          ? FILE_TABLE_ACTION_UPLOAD_HIS
          : FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });

      trackEvent({
        file_implementation: "HIS",
        name: TRACK_EVENTS.SELECT_FILE_CTA_CLICKED,
      });
    }
  }, [app, isTablet, hasActiveOnPrem, trackEvent, handleClick, setAction]);

  useEffect(() => {
    if (processedFile) {
      setAction({
        data: {} as FileFeatureData,
        type: FILE_TABLE_ACTION_UPLOAD,
      });
    }
  }, [processedFile, setAction]);

  useEffect(() => {
    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const renderModalContent = () => {
    switch (action?.type) {
      case FILE_TABLE_ACTION_UPLOAD_HIS:
        return (
          <HISFileUploadScreen
            availableFiles={availableFiles}
            getFilesHISProgress={getFilesHISProgress}
            mutationProgress={mutationProgress}
            setAction={setAction}
            unavailableFiles={unavailableFiles}
          />
        );
      case FILE_TABLE_ACTION_UPLOAD_DRAGNDROP:
        return (
          <DragNDropScreen handleChange={handleChange} setAction={setAction} />
        );
      case FILE_TABLE_ACTION_UPLOAD:
        return (
          <FileUploadScreen
            closeModal={close}
            handleSubmit={submit}
            initialValues={{
              category: null,
              fileName: processedFile?.file.name,
              kis_document_id_string: action?.data.kis_document_id_string,
              should_extract_on_upload: showExtractCheckbox,
            }}
            isOpen={isModalOpen}
            mutationProgress={mutationProgress}
            setAction={setAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex h-10 flex-col">
        <Button
          id="upload_file"
          startContent={<FileUpIcon />}
          tour={tourAttributes({
            stepKey: PRODUCT_TOURS.files_page.steps.select_files.key,
            tourKey: PRODUCT_TOURS.files_page.key,
          })}
          {...uploadButtonProps}
          className="z-0 m-0"
          color="primary"
          isDisabled={isDisabled}
          onPress={handleSelectFileV2}
        >
          {uploadButtonProps?.children ??
            translations.fileSection.fileUploadComponent.uploadFile}
        </Button>

        <div className="flex flex-col">
          <input
            ref={inputRef}
            accept={(allowedFileTypes ?? ALLOWED_FILE_MIME_TYPES).join(",")}
            className="hidden"
            data-testid="file-input"
            disabled={isDisabled}
            name="name"
            type="file"
            onChange={(event) => handleChange(event.target.files?.[0])}
          />
        </div>
      </div>

      <Modal
        classNames={{
          backdrop: "!z-modal",
          wrapper: "!z-modal",
        }}
        disableAnimation={wasModalOpenRef.current && isModalOpen}
        isDismissable={false}
        isOpen={isModalOpen}
        placement="center"
        size="3xl"
        onClose={() => {
          trackEvent({
            cta: "outside_modal",
            file_implementation: "HIS",
            name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
          });
          close();
        }}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
}
